import React, {useCallback, useEffect, useRef, useState} from 'react';
import {CircularProgress, IconButton, InputAdornment, withStyles} from '@material-ui/core';
import {ArrowBack, Clear as ClearIcon, Search} from '@material-ui/icons';
import Dialog from '../../Dialog/Dialog';
import {Button} from '../../ui/Buttons';
import CloseIcon from '../../Icon/CloseIcon';
import MultiToggleButtons from '../../Button/MultiToggleButtons';
import InputSearch from '../../Input/Input';
import Scroll from '../../InfiniteScroll/Scroll';
import {findAllAgendamentoTipoPage} from '../../../services/AgendamentoTipoService';
import {findAllProcedimentos} from '../../../services/ProcedimentoService';
import {findAllLigacaoConvenio} from '../../../services/ConvenioService';

const PAGE_SIZE = 20;

const ModalConsultaConvenio = ({ classes, open, onClose }) => {
  const [tabSelected, setTabSelected] = useState(0);
  const [search, setSearch] = useState('');
  const [searchConvenio, setSearchConvenio] = useState('');
  const [itens, setItens] = useState([]);
  const [loadingItens, setLoadingItens] = useState(false);
  const [lastItens, setLastItens] = useState(false);
  const pageItensRef = useRef(0);
  const loadingItensRef = useRef(false);
  const tabRef = useRef(0);
  const searchRef = useRef('');

  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [convenios, setConvenios] = useState([]);
  const [loadingConvenios, setLoadingConvenios] = useState(false);
  const [lastConvenios, setLastConvenios] = useState(false);
  const pageConveniosRef = useRef(0);
  const loadingConveniosRef = useRef(false);
  const searchConvenioRef = useRef('');
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    if (open) resetAndLoadItens(0, '');
  }, [open]);

  // Debounce para busca de convênios
  const debouncedSearchConvenios = useCallback((searchTerm, itemSelecionado) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      resetAndLoadConvenios(searchTerm, itemSelecionado);
    }, 500);
  }, []);

  useEffect(() => {
    if (itemSelecionado) {
      debouncedSearchConvenios(searchConvenio, itemSelecionado);
    }
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchConvenio, itemSelecionado, debouncedSearchConvenios]);

  const resetAndLoadItens = (tab, searchValue) => {
    pageItensRef.current = 0;
    setItens([]);
    setLastItens(false);
    fetchItens(tab, searchValue, 0, true);
  };

  const resetAndLoadConvenios = (searchTerm, item) => {
    pageConveniosRef.current = 0;
    setConvenios([]);
    setLastConvenios(false);
    fetchConvenios(searchTerm, item, 0, true);
  };

  const fetchItens = async (tab, searchValue, page, reset = false) => {
    if (loadingItensRef.current) return;
    loadingItensRef.current = true;
    setLoadingItens(true);
    const pageableDTO = { pageNumber: page, pageSize: PAGE_SIZE, sortDir: 'ASC', sortField: tab === 0 ? 'nome' : 'descricao' };
    
    try {
      let result;
      if (tab === 0) {
        // Aba Procedimento
        const response = await findAllProcedimentos({
          nome: searchValue,
          ativo: true,
          pageableDTO
        });
        result = response.data.data.findAllProcedimentos;
      } else {
        // Aba Tipo de atendimento
        const response = await findAllAgendamentoTipoPage(pageableDTO, searchValue, true);
        result = response.data.data.findAllAgendamentoTipoPage;
      }
      
      const { content, last } = result;
      setItens(prev => reset ? content : [...prev, ...content]);
      setLastItens(last);
      pageItensRef.current = page + 1;
    } finally {
      loadingItensRef.current = false;
      setLoadingItens(false);
    }
  };

  const fetchConvenios = async (searchTerm, item, page, reset = false) => {
    if (loadingConveniosRef.current) return;
    loadingConveniosRef.current = true;
    setLoadingConvenios(true);
    
    try {
      // Usar findAllLigacaoConvenio para buscar convênios ligados ao item
      const tipoLigacao = tabRef.current === 0 ? 'PROCEDIMENTO' : 'AGENDAMENTO_TIPO';
      const result = await findAllLigacaoConvenio({
        tipoLigacaoEntidadesValores: tipoLigacao,
        search: searchTerm,
        entidadeId: String(item.id),
        pageableDto: {
          pageNumber: page,
          pageSize: PAGE_SIZE,
          sortDir: 'ASC',
          sortField: 'descricao'
        }
      });
      
      const { content, last } = result;
      
      // Mapear os resultados para o formato esperado
      const conveniosComValor = content.map(ligacao => ({
        id: ligacao.id,
        descricao: ligacao.descricao,
        valor: ligacao.valor
      }));
      
      setConvenios(prev => reset ? conveniosComValor : [...prev, ...conveniosComValor]);
      setLastConvenios(last);
      pageConveniosRef.current = page + 1;
    } finally {
      loadingConveniosRef.current = false;
      setLoadingConvenios(false);
    }
  };

  const handleLoadMoreItens = () => {
    if (loadingItensRef.current) return;
    fetchItens(tabRef.current, searchRef.current, pageItensRef.current, false);
  };

  const handleLoadMoreConvenios = () => {
    if (loadingConveniosRef.current) return;
    fetchConvenios(searchConvenioRef.current, itemSelecionado, pageConveniosRef.current, false);
  };

  const handleSearch = () => resetAndLoadItens(tabRef.current, search);

  const handleClear = () => {
    setSearch('');
    searchRef.current = '';
    resetAndLoadItens(tabRef.current, '');
  };

  const handleClearConvenio = () => {
    setSearchConvenio('');
    searchConvenioRef.current = '';
  };

  const handleSearchConvenio = () => {
    resetAndLoadConvenios(searchConvenio, itemSelecionado);
  };

  const handleChangeTab = (tab) => {
    tabRef.current = tab;
    setTabSelected(tab);
    setSearch('');
    searchRef.current = '';
    setSearchConvenio('');
    searchConvenioRef.current = '';
    resetAndLoadItens(tab, '');
  };

  const handleSelectItem = async (item) => {
    setItemSelecionado(item);
    setSearchConvenio('');
    searchConvenioRef.current = '';
  };

  const handleBack = () => {
    setItemSelecionado(null);
    setConvenios([]);
    setSearchConvenio('');
    searchConvenioRef.current = '';
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
  };

  const handleClose = () => {
    setItemSelecionado(null);
    setConvenios([]);
    setItens([]);
    setSearch('');
    setSearchConvenio('');
    setTabSelected(0);
    tabRef.current = 0;
    searchRef.current = '';
    searchConvenioRef.current = '';
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    onClose();
  };

  const formatValor = (valor) => {
    if (!valor && valor !== 0) return '-';
    return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const getItemNome = (item) => {
    return tabSelected === 0 ? item.nome : item.descricao;
  };

  const renderListaItens = () => (
    <>
      <MultiToggleButtons
        id='agd-ccv-btn-tab'
        options={['Procedimento', 'Tipo de atendimento']}
        tabSelected={tabSelected}
        changeTabSelected={handleChangeTab}
        classes={{ buttonsContainer: classes.tabsContainer }}
      />
      <div className={classes.searchContainer}>
        <InputSearch
          id='agd-ccv-inp-pes'
          startAdornment={
            <InputAdornment position="start">
              <IconButton id='agd-ccv-btn-pes' className={classes.iconBtn} onClick={handleSearch}>
                <Search />
              </IconButton>
            </InputAdornment>
          }
          endAdornment={
            search ? (
              <InputAdornment position="end">
                <IconButton id='agd-ccv-btn-lim' className={classes.iconBtn} onClick={handleClear}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          classInputRoot={classes.inputSearch}
          placeholder={tabSelected === 0 ? 'Pesquisar procedimento' : 'Pesquisar tipo de atendimento'}
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            searchRef.current = e.target.value;
          }}
          onKeyPress={e => e.key === 'Enter' && handleSearch()}
        />
      </div>
      <Scroll
        loadMore={handleLoadMoreItens}
        hasMore={!lastItens && !loadingItens}
        pageStart={0}
        initialLoad={false}
        className={classes.scrollList}
      >
        {itens.map(item => (
          <div
            key={item.id}
            className={classes.itemLista}
            onClick={() => handleSelectItem(item)}
          >
            <span className={classes.itemNome}>{getItemNome(item)}</span>
            <span className={classes.itemArrow}>›</span>
          </div>
        ))}
        {loadingItens && (
          <div className={classes.loading}><CircularProgress size={24} /></div>
        )}
        {!loadingItens && itens.length === 0 && (
          <div className={classes.semItens}>Nenhum item encontrado</div>
        )}
      </Scroll>
    </>
  );

  const renderConvenios = () => (
    <>
      <div className={classes.detalheHeader}>
        <IconButton id='agd-ccv-btn-vol' size="small" onClick={handleBack}>
          <ArrowBack />
        </IconButton>
        <span className={classes.itemTitulo}>{getItemNome(itemSelecionado)}</span>
      </div>
      <div className={classes.searchContainer}>
        <InputSearch
          id='agd-ccv-inp-pes-conv'
          startAdornment={
            <InputAdornment position="start">
              <IconButton id='agd-ccv-btn-pes-conv' className={classes.iconBtn} onClick={handleSearchConvenio}>
                <Search />
              </IconButton>
            </InputAdornment>
          }
          endAdornment={
            searchConvenio ? (
              <InputAdornment position="end">
                <IconButton id='agd-ccv-btn-lim-conv' className={classes.iconBtn} onClick={handleClearConvenio}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          classInputRoot={classes.inputSearch}
          placeholder="Pesquisar convênio"
          value={searchConvenio}
          onChange={e => {
            setSearchConvenio(e.target.value);
            searchConvenioRef.current = e.target.value;
          }}
          onKeyPress={e => e.key === 'Enter' && handleSearchConvenio()}
        />
      </div>
      <div className={classes.tabelaHeader}>
        <span className={classes.tabelaTitleNome}>Convênio</span>
        <span className={classes.tabelaTitleValor}>Valor</span>
      </div>
      <Scroll
        loadMore={handleLoadMoreConvenios}
        hasMore={!lastConvenios && !loadingConvenios}
        pageStart={0}
        initialLoad={false}
        className={classes.scrollList}
      >
        {convenios.map((convenio, idx) => (
          <div key={idx} className={classes.convenioLinha}>
            <span className={classes.convenioNome}>{convenio.descricao}</span>
            <span className={classes.convenioValor}>{formatValor(convenio.valor)}</span>
          </div>
        ))}
        {loadingConvenios && (
          <div className={classes.loading}><CircularProgress size={24} /></div>
        )}
        {!loadingConvenios && convenios.length === 0 && (
          <div className={classes.semItens}>Nenhum convênio encontrado</div>
        )}
      </Scroll>
    </>
  );

  return (
    <Dialog
      open={open}
      classes={{ paper: classes.paper, paperScrollPaper: classes.paperScrollPaper }}
      scroll="paper"
      maxWidth="sm"
      fullWidth
    >
      <div className={classes.tituloFechar}>
        <span className={classes.titulo}>Consultar convênios</span>
        <Button
          id='agd-ccv-btn-fch'
          shape="circle"
          bgColor="#FB7676"
          onClick={handleClose}
        >
          <CloseIcon />
        </Button>
      </div>
      <div className={classes.content}>
        {itemSelecionado ? renderConvenios() : renderListaItens()}
      </div>
    </Dialog>
  );
};

const styles = {
  paper: {
    width: '560px',
    maxWidth: '100%',
    height: '75vh',
    background: '#fff',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  paperScrollPaper: { maxHeight: '100%' },
  tituloFechar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    flexShrink: 0,
  },
  titulo: {
    fontWeight: 700,
    fontSize: '18px',
    color: '#333333',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '16px',
    gap: '12px',
    minHeight: 0,
  },
  tabsContainer: { margin: 0 },
  searchContainer: { flexShrink: 0 },
  inputSearch: {
    background: '#F2F2F2',
    border: '0.5px solid rgba(220,220,220,0.2)',
    borderRadius: '8px',
    fontFamily: 'Poppins',
    height: '38px',
    fontSize: '14px',
  },
  iconBtn: { padding: '4px' },
  scrollList: {
    flex: 1,
    minHeight: 0,
    maxHeight: 'unset !important',
    overflowY: 'auto !important',
  },
  itemLista: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 8px',
    borderRadius: '8px',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    '&:hover': { background: '#f5f5f5' },
  },
  itemNome: { fontSize: '14px', color: '#333', fontWeight: 500 },
  itemArrow: { color: '#868686', fontSize: '20px' },
  loading: { display: 'flex', justifyContent: 'center', padding: '16px' },
  semItens: {
    textAlign: 'center',
    color: '#868686',
    fontSize: '14px',
    padding: '32px 0',
  },
  detalheHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  itemTitulo: { fontWeight: 600, fontSize: '15px', color: '#333' },
  tabelaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 8px',
    flexShrink: 0,
  },
  tabelaTitleNome: { fontSize: '10px', color: '#868686' },
  tabelaTitleValor: { fontSize: '10px', color: '#868686', width: '90px', textAlign: 'right' },
  convenioLinha: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 8px',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },
  convenioNome: { fontSize: '14px', color: '#868686', flex: 1 },
  convenioValor: {
    fontSize: '14px',
    color: '#333',
    fontWeight: 500,
    width: '90px',
    textAlign: 'right',
  },
};

export default withStyles(styles)(ModalConsultaConvenio);